import { Alert, Button, Dialog, DialogActions, DialogContent, DialogTitle, Stack } from '@mui/material';
import type { ReactNode } from 'react';

import { useT } from '../i18n/LanguageContext';

import { ErrorNotice } from './ErrorNotice';

type FormDialogProps = {
  title: string;
  onClose: () => void;
  onSubmit: () => void;
  submitLabel?: string;
  isBusy?: boolean;
  /** What the API answered. */
  error?: unknown;
  /** What the form itself could not accept, in words already translated. */
  problem?: string | null;
  maxWidth?: 'xs' | 'sm' | 'md' | 'lg';
  children: ReactNode;
};

/**
 * Every form in a dialog. It is mounted when opened and unmounted when closed, so a dialog never shows the
 * last attempt's values — and a stock operation gets a fresh idempotency key each time it opens.
 */
export const FormDialog = ({
  title,
  onClose,
  onSubmit,
  submitLabel,
  isBusy = false,
  error,
  problem,
  maxWidth = 'sm',
  children,
}: FormDialogProps) => {
  const t = useT();

  return (
    <Dialog open onClose={onClose} fullWidth maxWidth={maxWidth}>
      <form
        onSubmit={(event) => {
          event.preventDefault();
          onSubmit();
        }}
      >
        <DialogTitle>{title}</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ pt: 1 }}>
            <ErrorNotice error={error} />
            {problem ? (
              <Alert severity="warning" variant="outlined">
                {problem}
              </Alert>
            ) : null}
            {children}
          </Stack>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={onClose}>{t('common.cancel')}</Button>
          <Button type="submit" variant="contained" disabled={isBusy}>
            {submitLabel ?? t('common.save')}
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  );
};
